import { MenuCompositionStructureSetting } from 'features/CalorieManagement/MenuStructureSetting/models';
import { Button, Form, Table } from 'react-bootstrap';
import { EDIT_TABLE_HEADER } from '../constants/tableHeader';
import { useTranslation } from 'react-i18next';
import { FieldErrors, FieldNamesMarkedBoolean, UseFormRegister } from 'react-hook-form';
import { MenuStructureSettingForm } from '..';
import DatePickerCustom from 'components/common/DatePickerCustom/DatePickerCustom';
import { stringToDateFormat } from 'utils';

const EditTable = ({
  compositions,
  disabled,
  onOpenAddCompositionModal,
  register,
  errors,
  onRemoveComposition,
  elementUnitMasters,
  purchaseUnitMasters,
  dirtyFields,
  setValue,
  watch,
  trigger,
}: {
  compositions: MenuCompositionStructureSetting[];
  disabled: boolean;
  register: UseFormRegister<MenuStructureSettingForm>;
  errors: FieldErrors<MenuStructureSettingForm>;
  dirtyFields: Partial<Readonly<FieldNamesMarkedBoolean<MenuStructureSettingForm>>>;
  onOpenAddCompositionModal: (code: string) => void;
  onRemoveComposition: (idx: number, composition: any) => void;
  elementUnitMasters: any[];
  purchaseUnitMasters: any[];
  setValue: any;
  watch: any;
  trigger: any;
}) => {
  const { t } = useTranslation();
  const CodeCell = ({ code }: { code: string }) => {
    return (
      <div
        style={{ color: '#3d8bfd', textDecoration: 'underline', cursor: 'pointer' }}
        onClick={() => onOpenAddCompositionModal(code)}
      >
        {code}
      </div>
    );
  };

  return (
    <div className="sticky-table flex-grow-1 flex-shrink-1">
      <Table>
        <thead>
          <tr>
            {EDIT_TABLE_HEADER.map((header, idx) => (
              <th style={{ width: header.width, zIndex: '10' }} key={idx}>
                {t(`MenuStructureSettingScreen.${header.title}`)}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {compositions.map((composition: any, idx: number) => {
            const effective_start_date = watch(`compositions.${idx}.effective_start_date`);
            const effective_end_date = watch(`compositions.${idx}.effective_end_date`);
            const isElement = composition.supplier_item_cd ? false : true;
            const filteredPurchaseUnitMasters = purchaseUnitMasters.filter(
              (unit) =>
                unit.purchase_unit_cd === composition.purchase_item_unit_cd ||
                unit.purchase_unit_cd === composition.process_item_unit_cd ||
                unit.purchase_unit_cd === composition?.supplementary_unit_cd_1 ||
                unit.purchase_unit_cd === composition?.supplementary_unit_cd_2 ||
                unit.purchase_unit_cd === composition?.supplementary_unit_cd_3
            );
            const filteredElementUnitMasters = elementUnitMasters.filter(
              (unit) =>
                unit.element_unit_cd === composition.element_unit_master ||
                unit.element_unit_cd === composition?.supplementary_unit_cd_1 ||
                unit.element_unit_cd === composition?.supplementary_unit_cd_2 ||
                unit.element_unit_cd === composition?.supplementary_unit_1 ||
                unit.element_unit_cd === composition?.supplementary_unit_2
            );
            const isChange = composition.isAdded
              ? composition.change_flag
              : composition.change_flag ||
                dirtyFields?.compositions?.[idx]?.usage_amount ||
                dirtyFields?.compositions?.[idx]?.effective_end_date ||
                dirtyFields?.compositions?.[idx]?.effective_start_date ||
                dirtyFields?.compositions?.[idx]?.sort_key ||
                dirtyFields?.compositions?.[idx]?.remarks ||
                dirtyFields?.compositions?.[idx]?.display_group ||
                dirtyFields?.compositions?.[idx]?.unit;

            if (composition.isRemovedFromFilter || composition.isRemoved) return <></>;
            return (
              <tr key={composition.id}>
                <td>
                  {isChange ? (
                    <div className="change-dot-container">
                      <div className="change-dot"></div>
                    </div>
                  ) : (
                    <></>
                  )}
                </td>
                <td>
                  <CodeCell code={composition.code} />
                </td>
                <td>{composition.name}</td>
                <td className="text-center">
                  <div className="d-flex justify-content-between">
                    <div>
                      <div className="d-flex justify-content-between align-middle">
                        <Form.Control
                          key={composition.id}
                          step={'0.01'}
                          style={{ width: 95 }}
                          isInvalid={Boolean(errors?.compositions?.[idx]?.usage_amount)}
                          {...register(`compositions.${idx}.usage_amount`)}
                          type="number"
                          min="0"
                          disabled={disabled}
                          onKeyDown={(evt) =>
                            (evt.key === 'e' || evt.key === '-' || evt.key === 'E' || evt.key === '+') &&
                            evt.preventDefault()
                          }
                        />
                      </div>
                      <div className="text-start">
                        {errors?.compositions?.[idx]?.usage_amount && (
                          <span className="small text-danger d-inline-block mt-1">
                            {errors?.compositions?.[idx]?.usage_amount?.message}
                          </span>
                        )}
                      </div>
                    </div>
                    <div style={{ marginLeft: '5px' }}>
                      <div className="d-flex justify-content-between align-middle">
                        <Form.Select
                          key={composition.id}
                          isInvalid={Boolean(errors?.compositions?.[idx]?.unit)}
                          style={{ width: 100 }}
                          {...register(`compositions.${idx}.unit`)}
                          disabled={disabled}
                        >
                          {(isElement ? filteredElementUnitMasters : filteredPurchaseUnitMasters).map(
                            (option: any, idx: number) => {
                              return (
                                <option key={idx} value={isElement ? option.element_unit_cd : option.purchase_unit_cd}>
                                  {option.name}
                                </option>
                              );
                            }
                          )}
                        </Form.Select>
                      </div>
                      <div className="text-start">
                        {errors?.compositions?.[idx]?.unit && (
                          <span className="small text-danger d-inline-block mt-1">
                            {errors?.compositions?.[idx]?.unit?.message}
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                </td>
                <td className="text-end ">
                  <div className="d-flex justify-content-between align-middle">
                    <Form.Control
                      key={composition.id}
                      style={{ width: 80 }}
                      isInvalid={Boolean(errors?.compositions?.[idx]?.sort_key)}
                      {...register(`compositions.${idx}.sort_key`)}
                      type="number"
                      min="0"
                      disabled={disabled}
                      onKeyDown={(evt) =>
                        (evt.key === 'e' || evt.key === '-' || evt.key === 'E' || evt.key === '+') &&
                        evt.preventDefault()
                      }
                    />
                  </div>
                  <div className="text-start">
                    {errors?.compositions?.[idx]?.sort_key && (
                      <span className="small text-danger d-inline-block mt-1">
                        {errors?.compositions?.[idx]?.sort_key?.message}
                      </span>
                    )}
                  </div>
                </td>
                <td className="text-end ">
                  <div className="d-flex justify-content-between align-middle">
                    <Form.Control
                      key={composition.id}
                      style={{ width: 70 }}
                      isInvalid={Boolean(errors?.compositions?.[idx]?.display_group)}
                      {...register(`compositions.${idx}.display_group`)}
                      type="number"
                      min="0"
                      disabled={disabled}
                      onKeyDown={(evt) =>
                        (evt.key === 'e' || evt.key === '-' || evt.key === 'E' || evt.key === '+') &&
                        evt.preventDefault()
                      }
                    />
                  </div>
                  <div className="text-start">
                    {errors?.compositions?.[idx]?.display_group && (
                      <span className="small text-danger d-inline-block mt-1">
                        {errors?.compositions?.[idx]?.display_group?.message}
                      </span>
                    )}
                  </div>
                </td>
                <td className="text-end ">
                  <div className="d-flex justify-content-between align-middle">
                    <DatePickerCustom
                      name={`compositions.${idx}.effective_start_date`}
                      value={effective_start_date}
                      onChange={(date: any) => {
                        setValue(`compositions.${idx}.effective_start_date`, date, {
                          shouldValidate: true,
                          shouldDirty: true,
                        });

                        trigger([`compositions.${idx}.effective_start_date`, `compositions.${idx}.effective_end_date`]);
                      }}
                      onInPut={(date: any) => {
                        const formatDate = stringToDateFormat(date);
                        setValue(`compositions.${idx}.effective_start_date`, formatDate, {
                          shouldValidate: true,
                          shouldDirty: true,
                        });

                        trigger([`compositions.${idx}.effective_start_date`, `compositions.${idx}.effective_end_date`]);
                      }}
                      isInvalid={Boolean(errors?.compositions?.[idx]?.effective_start_date)}
                      disabled={disabled}
                    />
                  </div>
                  <div className="text-start">
                    {errors?.compositions?.[idx]?.effective_start_date && (
                      <span className="small text-danger d-inline-block mt-1">
                        {errors?.compositions?.[idx]?.effective_start_date?.message}
                      </span>
                    )}
                  </div>
                </td>
                <td className="text-end ">
                  <div className="d-flex justify-content-between align-middle">
                    <DatePickerCustom
                      name={`compositions.${idx}.effective_end_date`}
                      value={effective_end_date}
                      onChange={(date: any) => {
                        setValue(`compositions.${idx}.effective_end_date`, date, {
                          shouldValidate: true,
                          shouldDirty: true,
                        });

                        trigger([`compositions.${idx}.effective_start_date`, `compositions.${idx}.effective_end_date`]);
                      }}
                      onInPut={(date: any) => {
                        const formatDate = stringToDateFormat(date);
                        setValue(`compositions.${idx}.effective_end_date`, formatDate, {
                          shouldValidate: true,
                          shouldDirty: true,
                        });

                        trigger([`compositions.${idx}.effective_start_date`, `compositions.${idx}.effective_end_date`]);
                      }}
                      isInvalid={Boolean(errors?.compositions?.[idx]?.effective_end_date)}
                      disabled={disabled}
                    />
                  </div>
                  <div className="text-start">
                    {errors?.compositions?.[idx]?.effective_end_date && (
                      <span className="small text-danger d-inline-block mt-1">
                        {errors?.compositions?.[idx]?.effective_end_date?.message}
                      </span>
                    )}
                  </div>
                </td>
                <td className="text-end ">{composition.masterRemarks}</td>
                <td className="text-end ">
                  <Form.Control
                    key={composition.id}
                    isInvalid={Boolean(errors?.compositions?.[idx]?.remarks)}
                    {...register(`compositions.${idx}.remarks`)}
                    type="text-area"
                    disabled={disabled}
                  />
                </td>
                <td className="text-center ">
                  <Button onClick={() => onRemoveComposition(idx, composition)} disabled={disabled}>
                    {t('MenuStructureSettingScreen.delete')}
                  </Button>
                </td>
              </tr>
            );
          })}
        </tbody>
      </Table>
    </div>
  );
};

export { EditTable };
